import { Component, h, Prop, Element } from '@stencil/core';

@Component({
  tag: 'aafp-table',
  shadow: false,
})
export class AafpTable {
  @Element() element: HTMLElement;
  @Prop() mobileStyle: string;

  /**
   * Stores the table header text for generating
   * span data labels in the responsive mobile cell display
   */
  tableDataMap: Map<number, string> = new Map();

  /**
   * Table Selectors
   */
  ADL_TABLE: string = 'adl-table';
  LINK: string = 'adl-link';
  ODD_ROW: string = 'adl-table__row--odd';
  TABLE_HEADER_ROW: string = 'adl-table__header-row';
  MOBILE_CELL: string = 'adl-table__cell';
  EMPTY_CELL: string = 'adl-table__empty-cell';
  RESPONSIVE_ROWS: string = 'adl-table__responsive-rows';

  /**
   * Set custom AAFP table styles
   */
  setTableSelectors(table: HTMLTableElement | null, selectors: string[]): void {
    if (table) table.classList.add(...selectors);
  }

  /**
   * Add custom background color to odd rows
   */
  setDataRowSelectors(rows: HTMLTableRowElement[]): void {
    rows.forEach(({ classList }: HTMLTableRowElement, index: number) => {
      if (!(index % 2)) classList.add(this.ODD_ROW);
    });
  }

  /**
   * When the responsive row mobile style is selected,
   * this method loops through all the table rows,
   * and adds a span element to every cell to clarify
   * the header that corresponds to a data point.
   *
   * Or, if a cell is empty, this method adds a selector
   * to prevent it from displaying in mobile viewports.
   */
  setResponsiveTableCells(rows: HTMLTableRowElement[]): void {
    rows.forEach((row: HTMLTableRowElement) => {
      const cells = row.querySelectorAll('td');
      cells.forEach((cell: HTMLTableDataCellElement, index: number) => {
        const mobileCellLabel = document.createElement('b');
        mobileCellLabel.classList.add(this.MOBILE_CELL);
        if (this.tableDataMap.get(index)) {
          mobileCellLabel.innerText = `${this.tableDataMap.get(index)!.trim()}:`;
        }
        const { textContent } = cell;
        const canDisplayMobileCell = textContent && textContent.trim().length > 0;
        if (canDisplayMobileCell) cell.prepend(mobileCellLabel);
        if (!canDisplayMobileCell) cell.classList.add(this.EMPTY_CELL);
      });
    });
  }

  /**
   * Adds a border bottom color to table header rows
   */
  setHeaderRowSelectors(rows: HTMLTableRowElement[]): void {
    rows.forEach(({ classList }: HTMLTableRowElement) => classList.add(this.TABLE_HEADER_ROW));
  }

  /**
   * Ensures that any anchor elements inside the RTE table will look like AAFP links
   */
  setLinkSelectors(links: HTMLAnchorElement[]): void {
    links.forEach((link) => link.classList.add(this.LINK));
  }

  /**
   * Adds table header text content to the ES6 Map
   */
  setHeaderData(tableHeaders: HTMLTableHeaderCellElement[]): void {
    tableHeaders.forEach(({ textContent }: HTMLTableHeaderCellElement, index: number) =>
      this.tableDataMap.set(index, textContent!.toLowerCase() as string),
    );
  }

  /**
   * Do the following on componentWillLoad:
   * 1) Apply custom styles to table, th, tr and td elements
   * 2) Store responsive table header cell labels in ES6 Map
   * 3) Add textual markup to table cells if responsive mobile style is selected
   */
  componentWillLoad() {
    const rows = Array.from(this.element.querySelectorAll('tr'));
    this.setTableSelectors(this.element.querySelector('table'), [this.ADL_TABLE]);
    this.setLinkSelectors(Array.from(this.element.querySelectorAll('a')));
    this.setHeaderRowSelectors(rows.filter((row: HTMLTableRowElement) => row.querySelector('th')));
    this.setDataRowSelectors(rows.filter((row: HTMLTableRowElement) => row.querySelector('td')));
    if (this.mobileStyle === 'RESPONSIVE_ROWS') {
      this.setHeaderData(Array.from(this.element.querySelectorAll('th')));
      this.setResponsiveTableCells(rows);
      this.setTableSelectors(this.element.querySelector('table'), [this.RESPONSIVE_ROWS]);
    }
  }

  render() {
    return (
      <div class="adl-table adl-font__sans-serif">
        <slot name="aafp-table-data"></slot>
      </div>
    );
  }
}
